import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { fetchWithoutAuth } from '../libs/apiUtils'
import { 
  ArrowLeftIcon, 
  ArrowDownTrayIcon, 
  TableCellsIcon, 
  ChartBarIcon, 
  LockClosedIcon,
  GlobeAltIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import GlobalNavbar from '../components/GlobalNavbar'

interface Column {
  columnId: string | null
  columnName: string | null
  columnShownAtIndex: number | null
}

interface MetadataItem {
  name: string
  value: string
}

export default function DatasetDetail() {
  const { id } = useParams<{ id: string }>()

  const [table, setTable] = useState<any>(null)
  const [metadata, setMetadata] = useState<MetadataItem[]>([])
  const [columns, setColumns] = useState<Column[]>([])
  const [rows, setRows] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadDataset() {
      try {
        setLoading(true)
        const [detailRes, contentRes] = await Promise.all([
          fetchWithoutAuth(`${process.env.REACT_APP_BACKEND_URL}/api/public/table-detail`, {
            method: 'POST',
            body: JSON.stringify({ tableId: id })
          }),
          fetchWithoutAuth(`${process.env.REACT_APP_BACKEND_URL}/api/public/table-content/read`, {
            method: 'POST',
            body: JSON.stringify({ tableId: id })
          })
        ])

        const detailData = await detailRes.json()
        const contentData = await contentRes.json()

        if (detailRes.ok && detailData.success) {
          setTable(detailData.data)
        }

        if (contentRes.ok && contentData.success) {
          const rawRows = contentData.data?.rows || []
          const rawCols = contentData.data?.columns || []
          
          if (contentData.meta && Array.isArray(contentData.meta)) {
            setMetadata(contentData.meta)
          }

          const sortedCols = [...rawCols].sort((a, b) => (a.columnShownAtIndex ?? 0) - (b.columnShownAtIndex ?? 0))
          setColumns(sortedCols)

          const sortedRows = [...rawRows].sort((a, b) => (a.rowShownAtIndex ?? 0) - (b.rowShownAtIndex ?? 0))
          setRows(sortedRows)
        }
      } catch (err) {
        console.error('Failed to load dataset', err)
      } finally {
        setLoading(false)
      }
    }
    if (id) loadDataset()
  }, [id])

  const handleDownload = async () => {
    try {
      const headers: Record<string, string> = {}
      const token = localStorage.getItem('token')
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/table-content/template/public/${id}`, {
        headers
      })
      if (!res.ok) throw new Error('Gagal mengunduh dataset')

      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${table?.name || 'dataset'}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      toast.success('Dataset berhasil diunduh')
    } catch (err) {
      toast.error('Gagal mengunduh dataset')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center text-sm text-gray-500 font-medium">
        <TableCellsIcon className="size-5 text-blue-600 animate-spin mr-2" />
        Memuat dataset...
      </div>
    )
  }

  if (!table) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <h1 className="text-xl font-bold text-gray-900 mb-3">Dataset tidak ditemukan</h1>
        <Link className="text-sm text-blue-600 hover:underline flex items-center gap-1.5" to="/dataset">
          <ArrowLeftIcon className="size-4" /> Kembali ke daftar dataset
        </Link>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-sm">
      {/* Header */}
      <GlobalNavbar />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
          <nav className="flex items-center text-xs font-medium text-neutral-500">
            <Link className="hover:text-primary-600 transition-colors flex items-center gap-1" to="/dataset">
              <ArrowLeftIcon className="size-3" />
              Dataset
            </Link>
            <span className="mx-2 text-neutral-300">/</span>
            <span className="text-neutral-600">{table.category?.name || 'Kategori'}</span>
            <span className="mx-2 text-neutral-300">/</span>
            <span className="text-neutral-900 font-medium truncate max-w-xs">{table.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          
          {/* Main Content */}
          <div className="flex-1 min-w-0 w-full space-y-6">
            
            {/* Title & Desc */}
            <div className="bg-white p-6 rounded-xl shadow-xs border border-gray-200">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {table.isPublic ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <GlobeAltIcon className="size-3" /> Publik
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                    <LockClosedIcon className="size-3" /> Privat
                  </span>
                )}
                {table.category?.name && (
                  <span className="px-2.5 py-0.5 rounded-md text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200">
                    {table.category.name}
                  </span>
                )}
              </div>

              <h1 className="text-2xl font-bold text-gray-900 mb-2">{table.name}</h1>
              <p className="text-gray-600 leading-relaxed mb-6">
                {table.description && table.description !== '-' 
                  ? table.description 
                  : 'Tidak ada deskripsi yang tersedia untuk dataset ini.'}
              </p>
              
              <div className="flex flex-wrap items-center gap-3 pt-5 border-t border-gray-100">
                <button 
                  onClick={handleDownload}
                  className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2.5 rounded-lg font-semibold transition-all active:scale-95"
                >
                  <ArrowDownTrayIcon className="size-4" />
                  Unduh Dataset (XLSX)
                </button>
              </div>
            </div>

            {/* Data Preview Box */}
            <div className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TableCellsIcon className="size-4 text-gray-500" />
                  <h3 className="font-semibold text-gray-900">Pratinjau Data</h3>
                </div>
                <span className="text-xs text-gray-500 font-medium bg-white px-2 py-0.5 rounded border border-gray-200">
                  5 Baris Pertama
                </span>
              </div>
              
              {/* Horizontal & Vertical Scroll Area */}
              <div className="w-full overflow-x-auto max-h-[450px] overflow-y-auto [scrollbar-width:thin]">
                <table className="w-full text-left border-collapse min-w-max">
                  <thead className="sticky top-0 bg-gray-50 z-10 shadow-2xs">
                    <tr>
                      <th className="px-3 py-2.5 border-b border-gray-200 text-xs font-semibold text-gray-400 uppercase w-10 text-center">
                        #
                      </th>
                      {columns.map(col => (
                        <th 
                          key={col.columnId || col.columnShownAtIndex} 
                          className="px-4 py-2.5 border-b border-gray-200 text-xs font-semibold text-gray-600 uppercase whitespace-nowrap"
                        >
                          {col.columnName}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-gray-500 italic">
                          Belum ada data tersedia
                        </td>
                      </tr>
                    ) : (
                      rows.slice(0, 5).map((row, rowIdx) => (
                        <tr key={row.rowId || rowIdx} className="hover:bg-primary-50/30 transition-colors">
                          <td className="px-3 py-2.5 text-center text-xs font-medium text-neutral-400 border-r border-neutral-100 bg-neutral-50/20">
                            {rowIdx + 1}
                          </td>
                          {columns.map(col => {
                            const cell = row.values?.find((v: any) => 
                              v.columnId ? v.columnId === col.columnId : v.columnShownAtIndex === col.columnShownAtIndex
                            )
                            return (
                              <td 
                                key={`${row.rowId || rowIdx}-${col.columnId || col.columnShownAtIndex}`} 
                                className="px-4 py-2.5 text-gray-800 border-r border-gray-50 last:border-r-0 whitespace-nowrap"
                              >
                                {cell?.value !== undefined && cell?.value !== null && cell?.value !== '' ? cell.value : '-'}
                              </td>
                            )
                          })}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Table Footer */}
              <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-100 text-center text-xs text-gray-500">
                Menampilkan {Math.min(5, rows.length)} baris pertama dari total {rows.length} baris. Unduh dataset untuk melihat seluruh isi data.
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <div className="w-full lg:w-72 flex-shrink-0 space-y-5">
            <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 border-b border-gray-200 pb-3">
                <ChartBarIcon className="size-4 text-gray-400" />
                Informasi Dataset
              </h3>
              
              <dl className="space-y-3.5">
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Instansi</dt>
                  <dd className="text-gray-900 font-medium mt-0.5">
                    {table.admin.fullName || '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Grup Dataset</dt>
                  <dd className="text-gray-900 font-medium mt-0.5">
                    {table.category?.group?.name || '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Kategori</dt>
                  <dd className="text-gray-900 font-medium mt-0.5">
                    {table.category?.name || '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Diperbarui Pada</dt>
                  <dd className="text-gray-900 font-medium mt-0.5">
                    {table.updatedAt 
                      ? new Date(table.updatedAt).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' }) 
                      : '-'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Akses</dt>
                  <dd className="text-gray-900 font-medium mt-0.5">
                    {table.isPublic ? 'Publik' : 'Privat'}
                  </dd>
                </div>

                {/* Render Dynamic Metadata */}
                {metadata.length > 0 && (
                  <div className="pt-3 border-t border-gray-200 space-y-3.5">
                    {metadata.map((metaItem, idx) => (
                      <div key={idx}>
                        <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wide flex items-center gap-1">
                          <InformationCircleIcon className="size-3 text-primary-500" />
                          {metaItem.name}
                        </dt>
                        <dd className="text-gray-900 font-semibold mt-0.5">
                          {metaItem.value || '-'}
                        </dd>
                      </div>
                    ))}
                  </div>
                )}
              </dl>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
